<script>
  import { applicationLimitationStore } from "@stores/applicationLimitationStore.js";
  import {
    Modal as DangerModal,
    ToastNotification,
  } from "carbon-components-svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import AppIcon from "./AppIcon.svelte";
  import Modal from "@components/Modal.svelte";
  import { TextInput, SelectItem, Select } from "paxray-components-svelte";

  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";

  import EditIcon from "../../settings/svg/EditIcon.svelte";
  import TrashCan16 from "carbon-icons-svelte/lib/TrashCan16";
  import { fly } from "svelte/transition";
  import { PLUS_ICON } from "@utils/icons.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import {
    appMatches,
    appProcessName,
    findPresetMatch,
    isValidWebsite,
    normalizeRuleValue,
    websiteMatches,
  } from "lib/util/restrictionRules.js";

  export let editable = false;
  export let addedApplications = [];
  export let showTypeSelector = true;
  export let showAddIcon = true;
  // false renders only the dialogs, so a page empty state can open "Add App or Website" via openAddDialog
  export let showTable = true;
  // true while an entry is saved or deleted, for the SaveStatus of the page
  export let saving = false;
  // false when the page shows saving in its own SaveStatus, errors still come as a toast
  export let showSuccessToast = true;
  // Ids of the selected exclusion presets. New entries they already cover are blocked.
  // null when the page has no presets, then a preset match is only a hint.
  export let activePresetIds = null;
  // Participant exclusions of the project. New entries they already cover are blocked.
  export let participantEntries = [];
  let pendingSaves = 0;
  async function trackSave(request) {
    pendingSaves += 1;
    saving = true;
    try {
      return await request;
    } finally {
      pendingSaves -= 1;
      saving = pendingSaves > 0;
    }
  }

  //let applicationLimitationType = "Blacklist";
  let currentApplicationLimitation = {};
  // Ohne Name und App-Name bzw. URL entstand ein leerer Eintrag; der Fenstertitel bleibt optional
  $: limitationValid =
    Boolean(currentApplicationLimitation.name?.trim()) &&
    Boolean(
      (currentApplicationLimitation.isbrowser
        ? currentApplicationLimitation.url
        : currentApplicationLimitation.appname
      )?.trim(),
    );
  // Doppelte Eintraege schon im Dialog abfangen, der bearbeitete Eintrag zaehlt dabei nicht mit
  $: duplicateName = applicationLimitations.some(
    (existing) =>
      String(existing.id) !== String(currentApplicationLimitation.id) &&
      String(existing.name || "").trim().toLowerCase() ===
        String(currentApplicationLimitation.name || "").trim().toLowerCase() &&
      Boolean(currentApplicationLimitation.name?.trim()),
  );
  $: duplicateEntry =
    limitationValid && isAlreadyListed(currentApplicationLimitation);
  $: websiteInvalid =
    Boolean(currentApplicationLimitation.isbrowser) &&
    Boolean(currentApplicationLimitation.url?.trim()) &&
    !isValidWebsite(currentApplicationLimitation.url);

  // Save stays locked while the entry cannot be saved, the tooltip names the reason.
  $: saveBlockedReason = !currentApplicationLimitation.name?.trim()
    ? "Enter a name first."
    : currentApplicationLimitation.isbrowser
      ? !currentApplicationLimitation.url?.trim()
        ? "Enter the website address first."
        : websiteInvalid
          ? "Enter a valid website address, e.g. amazon.com"
          : ""
      : !appProcessName(currentApplicationLimitation.appname)
        ? "Enter the application name first."
        : "";
  $: saveBlockedTooltip =
    saveBlockedReason ||
    (duplicateName
      ? "An entry with this name already exists."
      : duplicateEntry
        ? currentApplicationLimitation.isbrowser
          ? "This website is already in the list with the same window title."
          : "This app is already in the list with the same window title."
        : coveredReason);

  // The website error shows once the field is left, and disappears while typing.
  let websiteErrorShown = false;
  $: urlValue = currentApplicationLimitation.url;
  $: urlValue, (websiteErrorShown = false);

  // Blacklist presets, for the hint that a category already covers the entry.
  let blacklistPresetGroups = null;
  async function loadBlacklistPresets() {
    if (blacklistPresetGroups) return;
    try {
      const response = await fetchWithSession(
        "/api/damprojects/getUserLimitations/Blacklist",
      );
      const data = await response.json();
      blacklistPresetGroups = data;
    } catch (err) {
      console.error("Error loading exclusion presets:", err);
      blacklistPresetGroups = { desktop: [], webapp: [] };
    }
  }
  $: if (applicationLimitationModalOpen && applicationLimitationType === "Blacklist")
    loadBlacklistPresets();
  $: presetGroupsForType =
    applicationLimitationType === "Blacklist" && blacklistPresetGroups
      ? currentApplicationLimitation.isbrowser
        ? blacklistPresetGroups.webapp
        : blacklistPresetGroups.desktop
      : [];
  $: presetValue = currentApplicationLimitation.isbrowser
    ? currentApplicationLimitation.url
    : currentApplicationLimitation.appname;
  $: presetMatcher = currentApplicationLimitation.isbrowser
    ? websiteMatches
    : appMatches;
  $: activePresetSet = new Set((activePresetIds || []).map(String));
  $: activePresetMatch = activePresetIds
    ? findPresetMatch(
        presetGroupsForType.filter((group) => activePresetSet.has(String(group.id))),
        presetValue,
        presetMatcher,
      )
    : null;
  $: presetMatch =
    activePresetMatch ||
    findPresetMatch(presetGroupsForType, presetValue, presetMatcher);

  // Same app or website and window title as a participant entry
  $: participantMatch = (participantEntries || []).find((entry) => {
    if (Boolean(entry.isbrowser) !== Boolean(currentApplicationLimitation.isbrowser))
      return false;
    if (entry.isbrowser)
      return (
        !!normalizeRuleValue(currentApplicationLimitation.url) &&
        normalizeRuleValue(entry.windowtitleorurl) ===
          normalizeRuleValue(currentApplicationLimitation.url)
      );
    return (
      !!appProcessName(currentApplicationLimitation.appname) &&
      appProcessName(entry.appname).toLowerCase() ===
        appProcessName(currentApplicationLimitation.appname).toLowerCase() &&
      String(entry.windowtitleorurl || "").trim().toLowerCase() ===
        String(currentApplicationLimitation.windowtitle || "").trim().toLowerCase()
    );
  });

  // Only new entries are blocked, existing ones stay editable
  $: isNewEntry = !currentApplicationLimitation.id;
  $: coveredReason = !isNewEntry
    ? ""
    : activePresetMatch
      ? `Already excluded by the selected preset ${activePresetMatch.group.name}.`
      : participantMatch
        ? "Already excluded by a participant exclusion."
        : "";

  function saveCurrentLimitation() {
    if (saveBlockedTooltip) return;
    // The recorder compares process names without the extension (mspaint, not mspaint.exe).
    const limitation = currentApplicationLimitation.isbrowser
      ? currentApplicationLimitation
      : {
          ...currentApplicationLimitation,
          appname: appProcessName(currentApplicationLimitation.appname),
        };
    trackSave(
      saveApplicationLimitationItem(limitation, idLimitation, {
        quiet: !showSuccessToast,
      }),
    );
  }
  let applicationLimitations = [];
  let applicationLimitationModalOpen = false;
  let deleteConfirmationOpen = false;
  let limitationToDelete = null;
  let page = 1;
  let pageSize = 10;
  let searchValue = "";
  let applicationTypeFilter = 0;
  const applicationTypeFilterOptions = ["All", "Apps", "Websites"];

  const configuredColumnActions = {
    menu: [
      {
        icon: EditIcon,
        label: "Edit",
        onClick: (row) => editApplicationLimitationItem(row.id),
      },
      {
        icon: TrashCan16,
        label: "Delete",
        danger: true,
        onClick: (row) => {
          limitationToDelete = row;
          deleteConfirmationOpen = true;
        },
      },
    ],
  };

  $: ({ idLimitation } = $projectListStore);

  const {
    subscribe,
    getApplicationLimitations,
    saveApplicationLimitationItem,
    updateDAMLimitation,
    deleteApplicationLimitation,
    closeModal,
    editApplicationLimitationItem,
  } = applicationLimitationStore;

  $: ({
    applicationLimitations,
    applicationLimitationType,
    applicationLimitationModalOpen,
    currentApplicationLimitation,
    saveSuccessful,
    error,
    successMsg,
    errorMsg,
  } = $applicationLimitationStore);

  // Number of entries for the page, null until the list of this project has loaded
  export let entryCount = null;
  let loadedLimitation = null;

  $: {
    if (idLimitation) {
      getApplicationLimitations(idLimitation).then(
        () => (loadedLimitation = idLimitation),
      );
    }
  }
  $: entryCount =
    loadedLimitation && loadedLimitation === idLimitation
      ? applicationLimitations.length
      : null;

  function isAlreadyListed(application) {
    return applicationLimitations.some((existing) => {
      if (application.id && String(existing.id) === String(application.id))
        return false;
      if (Boolean(existing.isbrowser) !== Boolean(application.isbrowser))
        return false;
      const existingApp = existing.isbrowser
        ? existing.url
        : appProcessName(existing.appname);
      const newApp = application.isbrowser
        ? application.url
        : appProcessName(application.appname);
      return (
        String(existingApp || "").toLowerCase() ===
          String(newApp || "").toLowerCase() &&
        String(existing.windowtitle || "").toLowerCase() ===
          String(application.windowtitle || "").toLowerCase()
      );
    });
  }

  $: {
    if (addedApplications.length > 0) {
      addedApplications
        .filter((application) => !isAlreadyListed(application))
        .forEach((application) => {
          trackSave(
            saveApplicationLimitationItem(application, idLimitation, {
              quiet: !showSuccessToast,
            }),
          );
        });
      addedApplications = [];
    }
  }

  $: filteredApplicationLimitations = applicationLimitations.filter((row) => {
    const matchesType =
      applicationTypeFilter === 0 ||
      (applicationTypeFilter === 1 && !row.isbrowser) ||
      (applicationTypeFilter === 2 && row.isbrowser);
    if (!matchesType) return false;

    const query = searchValue.trim().toLowerCase();
    if (!query) return true;

    const name = row?.name || "";
    const appName = row?.isbrowser ? row?.url : row?.appname;
    const windowTitle = row?.windowtitle || "";

    return [name, appName, windowTitle].some((value) =>
      String(value || "")
        .toLowerCase()
        .includes(query),
    );
  });

  function appIconUrl(row) {
    const application = row?.isbrowser ? row?.url : row?.appname;
    return application
      ? `/api/damprojects/app-icon?application=${encodeURIComponent(application)}`
      : "";
  }

  $: tableHeaders = [
    { key: "name", value: "App/Website" },
    { key: "appname", value: "Technical Name/URL" },
    { key: "windowtitle", value: "Window Title (optional)" },
    ...(editable ? [{ key: "menu", value: "Actions" }] : []),
  ];

  export function openAddDialog() {
    editApplicationLimitationItem(0);
  }

  async function confirmDeleteApplicationLimitation() {
    if (!limitationToDelete?.id) return;
    await trackSave(
      deleteApplicationLimitation(limitationToDelete.id, {
        quiet: !showSuccessToast,
      }),
    );
    deleteConfirmationOpen = false;
    limitationToDelete = null;
  }
</script>

{#if showTable}
{#if showTypeSelector}
  <div>
    {#if editable}
      <Select
        labelText="Limit Application Recording"
        selected={applicationLimitationType}
        on:change={(event) => {
          updateDAMLimitation(idLimitation, event.target.value);
        }}
      >
        <SelectItem value="Whitelist" text="Whitelist" />
        <SelectItem value="Blacklist" text="Blacklist" />
      </Select>
    {:else}
      <span class="limitation-type-title">{applicationLimitationType}</span>
    {/if}
  </div>
{/if}
<DataTableBlack
  headers={tableHeaders}
  rows={filteredApplicationLimitations}
  totalItems={filteredApplicationLimitations.length}
  bind:page
  bind:pageSize
  pageSizes={[10, 25, 50]}
  {searchValue}
  slicePage
  sortable
  showNewButton={editable}
  newButtonText="Add App or Website"
  emptyText={applicationLimitations.length
    ? "No matching entries."
    : editable
      ? 'No entries yet. Use "Add App or Website" to add one.'
      : "No entries."}
  newButtonIconLeft={false}
  newButtonIcon={showAddIcon ? PLUS_ICON : null}
  columnActions={editable ? configuredColumnActions : {}}
  on:search={(e) => {
    searchValue = e.detail.value;
    page = 1;
  }}
  on:clearSearch={() => {
    searchValue = "";
    page = 1;
  }}
  on:addNew={() => {
    editApplicationLimitationItem(0);
  }}
>
  <div slot="left-toolbar" class="toolbar_content_switcher">
    <ContentSwitcher
      options={applicationTypeFilterOptions}
      bind:selected={applicationTypeFilter}
    />
  </div>
  <svelte:fragment slot="cell" let:row let:cell>
    {#if cell.key === "name"}
      <div class="app-website-cell">
        <AppIcon src={appIconUrl(row)} name={row.name} />
        <span>{cell.value || ""}</span>
      </div>
    {:else if cell.key === "appname"}
      {row.isbrowser ? row.url : row.appname}
    {:else if cell.key !== "menu"}
      {cell.value || ""}
    {/if}
  </svelte:fragment>
</DataTableBlack>
{/if}

{#if saveSuccessful}
  <div
    in:fly={{ x: 100 }}
    out:fly={{ y: 100 }}
    style="position:fixed; top:50px; right:20px; z-index:9999;"
  >
    <ToastNotification
      fullWidth
      hideAfter={3000}
      timeout={3000}
      kind="success"
      title="Success"
      subtitle={successMsg}
      caption={new Date().toLocaleString()}
      on:close={(e) => {
        e.preventDefault();
      }}
    />
  </div>
{/if}
{#if error}
  <div
    in:fly={{ x: 100 }}
    out:fly={{ y: 100 }}
    style="position:fixed; top:50px; right:20px; z-index:9999;"
  >
    <ToastNotification
      fullWidth
      kind="error"
      title="Error"
      subtitle={errorMsg}
      caption={new Date().toLocaleString()}
      on:close={(e) => {
        e.preventDefault();
      }}
    />
  </div>
{/if}

<Modal
  open={applicationLimitationModalOpen}
  preventCloseOnClickOutside
  modalHeading={`Add App or Website`}
  primaryButtonText="Save"
  primaryButtonValidations={!!saveBlockedTooltip}
  primaryButtonDisabledTooltip={saveBlockedTooltip}
  secondaryButtonText="Cancel"
  autowidth
  primaryButtonAction={saveCurrentLimitation}
  secondaryButtonAction={() => {
    closeModal();
  }}
  actions
  on:close={() => {
    closeModal();
  }}
  on:submit={saveCurrentLimitation}
>
  <div class="application-limitation-modal">
    <ContentSwitcher
      bind:selected={currentApplicationLimitation.isbrowser}
      options={["Desktop Application", "Web Application"]}
    ></ContentSwitcher>
    <TextInput
      labelText="Name"
      placeholder="Enter name for rule"
      bind:value={currentApplicationLimitation.name}
      invalid={duplicateName}
      invalidText="An entry with this name already exists."
      clearInvalidOnFocus={false}
    />
    {#if currentApplicationLimitation.isbrowser}
      <TextInput
        labelText="URL"
        placeholder="e.g. amazon.com"
        bind:value={currentApplicationLimitation.url}
        invalid={duplicateEntry ||
          !!coveredReason ||
          (websiteInvalid && websiteErrorShown)}
        invalidText={duplicateEntry
          ? "This website is already in the list with the same window title."
          : coveredReason || "Enter a valid website address, e.g. amazon.com"}
        description={presetMatch && !coveredReason
          ? `Also in the preset list ${presetMatch.group.name}, which is not selected.`
          : null}
        clearInvalidOnFocus={false}
        on:blur={() => (websiteErrorShown = true)}
      />
    {:else}
      <TextInput
        labelText="Application Name"
        placeholder="e.g. mspaint"
        bind:value={currentApplicationLimitation.appname}
        invalid={duplicateEntry || !!coveredReason}
        invalidText={duplicateEntry
          ? "This app is already in the list with the same window title."
          : coveredReason}
        description={presetMatch && !coveredReason
          ? `Also in the preset list ${presetMatch.group.name}, which is not selected.`
          : null}
        clearInvalidOnFocus={false}
      />
    {/if}
    <TextInput
      labelText="Window Title"
      placeholder="Optional, part of the window title"
      bind:value={currentApplicationLimitation.windowtitle}
    />
  </div>
</Modal>

<DangerModal
  danger
  bind:open={deleteConfirmationOpen}
  preventCloseOnClickOutside
  modalHeading="Remove from whitelist?"
  primaryButtonText="Remove from whitelist"
  secondaryButtonText="Cancel"
  on:click:button--primary={confirmDeleteApplicationLimitation}
  on:click:button--secondary={() => {
    deleteConfirmationOpen = false;
    limitationToDelete = null;
  }}
  on:close={() => {
    deleteConfirmationOpen = false;
    limitationToDelete = null;
  }}
>
  <p>
    Do you really want to remove the following App/Website from the whitelist?
  </p>
  <p><strong>{limitationToDelete?.name || "-"}</strong></p>
</DangerModal>

<style>
  .limitation-type-title {
    font-size: large;
    font-weight: bold;
    color: #333;
  }

  .app-website-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }


  .application-limitation-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
</style>
